import axios, { AxiosPromise } from 'axios';
import crypto from 'crypto-js';
import { DRAGYN_BASE_API_URL, SELFIE_FE_BASIC_AUTH, SELFIE_FE_CONFIG_KEY, api } from '../config/Master';
import { getGa } from '../common';
import { getUniqueDevice } from '../utils/ipQualityScore';
import { isEmpty } from 'lodash';
import { QA_SIMULATE_LIVENESS } from '../config/constants';

const createUserAccountV2 = (formData: any): AxiosPromise => {
  return axios.post(`${api}/account/create-v2`, formData);
};

const createUserAccount = (formData: any): AxiosPromise => {
  return axios.post(`${api}/account`, formData);
};

const getNewAuthToken = (tokenPath: any): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/auth/${tokenPath}`,
    timeout: 15000, // 15 second timeout to prevent hanging requests
    data: {},
  });
};

const killAuthToken = (): AxiosPromise => {
  return axios.post(`${api}/auth/logout`, null);
};

const requestPassReminder = (email: string): AxiosPromise => {
  return axios({
    method: 'post',
    url: `${api}/forgot/password`,
    data: {
      email: email,
      gaClientId: getGa(),
    },
  });
};

const getFirstAuthToken = (formData: any): AxiosPromise => {
  const deviceIdentifier = getUniqueDevice();

  if (deviceIdentifier.request_id) {
    formData.ipqs_device_id = deviceIdentifier.device_id;
    formData.ipqs_request_id = deviceIdentifier.request_id;
  }

  // include js fingerprint.
  return axios({
    method: 'post',
    url: `${api}/auth/login`,
    data: {
      ...formData,
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      fingerprint: window.fingerprint,
      gaClientId: getGa(),
    },
  });
};

const setNewPassword = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/reset/password`,
    data: {
      ...formData,
      gaClientId: getGa(),
    },
  });
};

const validateActivationPasswordToken = (token: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/account/migration/validate-token`,
    data: { token },
    // data: isProdEnv
    //   ? { token }
    //   : {
    //       token,
    //       retry: true,
    //     },
  });
};

const validateResetPasswordToken = (token: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/reset/password/validate-token`,
    data: { token },
  });
};

const deactivateAccount = (token: any, formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/account/deactivate`,
    data: formData,
  });
};

const resendEmail = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/account/resend_activation`,
    data: {},
  });
};

const attemptSocialLogin = (formData: any): AxiosPromise => {
  // include js fingerprint.
  return axios({
    method: 'post',
    url: `${api}/auth/social/validate-login`,
    data: {
      ...formData,
    },
  });
};

const attachSocialLogin = (token: any, formData: any): AxiosPromise => {
  if (formData.action === 'disconnect') {
    return axios({
      method: 'DELETE',
      url: `${api}/auth/social/attach-social`,
      data: formData,
    });
  }
  return axios({
    method: 'POST',
    url: `${api}/auth/social/attach-social`,
    data: formData,
  });
};

const validateSocialCredentials = (token: any, formData: any): AxiosPromise => {
  // include js fingerprint.
  return axios({
    method: 'post',
    url: `${api}/auth/social/validate-credentials`,
    data: formData,
  });
};

const getSignedLinkToken = (token: any): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/auth/validate-email-token?email_token=${token}`,
    data: {},
  });
};

const destroyOtherDeviceSession = (): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/settings/revoke-other-sessions`,
  });
};

const validateAutoSignup = (queryString: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/account/migrate`,
    data: queryString,
  });
};

const getVerificationStatus = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/id-verification`,
    data: {},
  });
};

const setVerificationStatus = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/id-verification`,
    data: formData,
  });
};

const requestOtpAuth = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/otp/auth`,
    data: formData,
  });
};

const requestOtpAuthVerify = (formData: any): AxiosPromise => {
  const deviceIdentifier = getUniqueDevice();

  if (deviceIdentifier.request_id) {
    formData.ipqs_device_id = deviceIdentifier.device_id;
    formData.ipqs_request_id = deviceIdentifier.request_id;
  }

  return axios({
    method: 'POST',
    url: `${api}/otp/auth/verify`,
    data: formData,
  });
};

const requestOtp = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/otp`,
    data: formData,
  });
};

const requestOtpVerify = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/otp/verify`,
    data: formData,
  });
};

const verifyEmail = (email: string, hCaptcha = ''): AxiosPromise => {
  const requestPayload = {
    email,
  };

  if (!isEmpty(hCaptcha)) {
    requestPayload['h-captcha-response'] = hCaptcha;
  }

  return axios({
    method: 'post',
    url: `${api}/auth/email/check`,
    data: requestPayload,
  });
};

const requestEmailOtp = (
  email: string,
  type: string,
  hCaptcha = '',
  isTestOTP = false,
  isResendOTP = false,
  isResetOTP = false
): AxiosPromise => {
  if (type === 'privateChangeEmail') {
    type = 'change_email';
  }

  const requestPayload = {
    email,
    type,
  };

  if (!isEmpty(hCaptcha)) {
    requestPayload['h-captcha-response'] = hCaptcha;
  }

  if (isTestOTP) {
    requestPayload['bypassOtp'] = true;
  }

  if (isResendOTP) {
    requestPayload['resend_otp'] = true;
  }

  if (isResetOTP) {
    requestPayload['reset_otp'] = true;
  }

  return axios({
    method: 'POST',
    url: `${api}/auth/email-otp/create`,
    data: requestPayload,
  });
};

const emailOtpVerify = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/auth/email-otp/verify`,
    data: formData,
  });
};

const resetEmailOtp = (email: string, type: string, hCaptcha = ''): AxiosPromise => {
  if (type === 'privateChangeEmail') {
    type = 'change_email';
  }

  const requestPayload = {
    email,
    type,
  };

  if (!isEmpty(hCaptcha)) {
    requestPayload['h-captcha-response'] = hCaptcha;
  }

  return axios({
    method: 'POST',
    url: `${api}/auth/email-otp/reset`,
    data: requestPayload,
  });
};

const verifyNewEmail = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/settings/validate-email`,
    data: formData,
  });
};

const authVerifyPassword = (formData: any): AxiosPromise => {
  const deviceIdentifier = getUniqueDevice();

  if (deviceIdentifier.request_id) {
    formData.ipqs_device_id = deviceIdentifier.device_id;
    formData.ipqs_request_id = deviceIdentifier.request_id;
  }

  // include js fingerprint.
  return axios({
    method: 'post',
    url: `${api}/auth/verify`,
    data: {
      ...formData,
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      fingerprint: window.fingerprint,
      gaClientId: getGa(),
    },
  });
};

/**
 * faceliveness result submit
 *@param {string} hashId
 * @param {object} data
 * @returns Axios
 */
const updateFacelivenessResult = (hashId: string, data: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/liveness/${hashId}/submit`,
    data,
  });
};

interface FaceLivelinessApiUrlResponse {
  data: {
    $metadata: {
      httpStatusCode: number;
      requestId: string;
      attempts: number;
      totalRetryDelay: number;
    };
    AuditImages: any;
    ReferenceImage: any;
    Confidence: number;
    SessionId: string;
    Status: string;
  };
}

const getQASimulateLiveness = (): string | null | undefined => {
  const queryParams = new URLSearchParams(window.location.search);

  return queryParams.get(QA_SIMULATE_LIVENESS);
};

const shaString = crypto.SHA1(SELFIE_FE_BASIC_AUTH).toString();
const selfieBasicAuthToken = btoa(shaString);

const postSelfieVerification = async (hashId: string, values: FaceLivelinessApiUrlResponse) => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Basic ${selfieBasicAuthToken}`,
    'X-API-KEY': SELFIE_FE_CONFIG_KEY,
  };

  const url = `${DRAGYN_BASE_API_URL}/selfie-verification/verify`;

  const qaSimulateLiveness = getQASimulateLiveness();

  const data = {
    ...values.data,
    userUid: hashId,
    isWeb: true,
    callbackUrl: `${api}/liveness/callback`,
    ...(!!qaSimulateLiveness && { qaSimulateLiveness }),
  };

  const result = await axios({
    url,
    method: 'POST',
    headers,
    data,
  });

  return result.data;
};

const generateMagicLink = async (hashId: string) => {
  return await axios({
    url: `${api}/liveness/magic-link/create`,
    method: 'POST',
    data: {
      account: hashId,
    },
  });
};

const verifyMagicLink = async data => {
  return await axios({
    url: `${api}/liveness/magic-link/verify`,
    method: 'POST',
    data,
  });
};

/**
 * faceliveness skip api
 *@param {string} hashId
 * @param {object} data
 * @returns Axios
 */
const selfieVerificationSkip = (hashId: string): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/liveness/${hashId}/skip`,
  });
};

export {
  createUserAccountV2,
  createUserAccount,
  getNewAuthToken,
  killAuthToken,
  requestPassReminder,
  getFirstAuthToken,
  setNewPassword,
  validateActivationPasswordToken,
  validateResetPasswordToken,
  deactivateAccount,
  resendEmail,
  attemptSocialLogin,
  attachSocialLogin,
  validateSocialCredentials,
  getSignedLinkToken,
  destroyOtherDeviceSession,
  validateAutoSignup,
  getVerificationStatus,
  setVerificationStatus,
  requestOtp,
  requestOtpVerify,
  requestOtpAuth,
  requestOtpAuthVerify,
  verifyEmail,
  requestEmailOtp,
  emailOtpVerify,
  verifyNewEmail,
  resetEmailOtp,
  authVerifyPassword,
  postSelfieVerification,
  updateFacelivenessResult,
  generateMagicLink,
  verifyMagicLink,
  selfieVerificationSkip,
};
